import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { AutoTaskAuthContext } from "../../../../Context/AutoTaskContext";
import {
  departmentService,
  MEMBERSHIP_ROLES,
  readApiError,
} from "../../services/departmentService";

const BLUE = {
  50: "#e8f6f7",
  100: "#c2e6ea",
  600: "#0c8a9b",
  700: "#0a7480",
  900: "#0f172a",
};
const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

const initialsFor = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] || "")
    .join("")
    .toUpperCase() || "?";

const MemberAssignDialog = ({
  open,
  onClose,
  departmentId,
  node, // null means the membership covers the whole department
  breadcrumb = [],
  existingMembers = [],
  store_id,
  user_id,
  onSaved,
}) => {
  const contextData = useContext(AutoTaskAuthContext) || {};
  const { allUsers } = contextData;

  const [selectedUser, setSelectedUser] = useState(null);
  const [role, setRole] = useState("MEMBER");
  const [isPrimary, setIsPrimary] = useState(false);
  const [placements, setPlacements] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // A person sits on a level once. Offering someone already here would only
  // produce a unique-constraint error, so they are filtered out of the list and
  // the existing row is edited instead.
  const takenIds = new Set(existingMembers.map((member) => member.user_id));
  const options = (allUsers || []).filter(
    (user) => user.status === "active" && !takenIds.has(user.id),
  );

  useEffect(() => {
    if (!open) return;
    setSelectedUser(null);
    setRole("MEMBER");
    setIsPrimary(false);
    setPlacements(null);
    setError("");
    setSaving(false);
  }, [open]);

  // Where else this person already sits. Shown to reassure, never to warn —
  // being in several departments is the point, not a mistake.
  useEffect(() => {
    if (!selectedUser?.id) {
      setPlacements(null);
      return;
    }
    let cancelled = false;
    departmentService
      .getUserPlacements(selectedUser.id)
      .then((res) => {
        if (!cancelled) setPlacements(res?.data?.data || []);
      })
      .catch(() => {
        // Nothing here is load-bearing, so a failure just hides the hint.
        if (!cancelled) setPlacements(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser]);

  const handleSubmit = async () => {
    if (!selectedUser?.id) {
      setError("Pick someone to add");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await departmentService.addMember({
        store_id,
        department_id: departmentId,
        dept_node_id: node?.id || null,
        user_id: selectedUser.id,
        membership_role: role,
        is_primary: isPrimary,
        assigned_by: user_id,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(readApiError(err, "Could not add this person. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const target = node ? node.name : "the whole department";
  const trail = breadcrumb.map((item) => item.name).join(" › ");

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { sx: { borderRadius: "14px" } } }}
    >
      <DialogTitle
        sx={{ fontFamily: fontFam, fontWeight: 700, color: BLUE[900], pb: 0.5 }}
      >
        Add someone to {target}
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          pt: "12px !important",
        }}
      >
        {trail && (
          <Typography
            sx={{
              fontFamily: fontFam,
              fontSize: 12.5,
              color: "#64748b",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              px: 1.25,
              py: 0.75,
            }}
          >
            {trail}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ fontFamily: fontFam }}>
            {error}
          </Alert>
        )}

        <Autocomplete
          options={options}
          value={selectedUser}
          onChange={(event, value) => setSelectedUser(value)}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No one left to add here"
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{ display: "flex", gap: 1.25, alignItems: "center" }}
            >
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  fontSize: 11,
                  fontFamily: fontFam,
                  fontWeight: 700,
                  bgcolor: BLUE[100],
                  color: BLUE[700],
                }}
              >
                {initialsFor(option.name)}
              </Avatar>
              <Typography sx={{ fontFamily: fontFam, fontSize: 14 }}>
                {option.name}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Person"
              required
              autoFocus
              InputLabelProps={{ sx: { fontFamily: fontFam } }}
            />
          )}
        />

        {placements !== null && placements.length > 0 && (
          <Box
            sx={{
              bgcolor: BLUE[50],
              border: `1px solid ${BLUE[100]}`,
              borderRadius: "10px",
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography
              sx={{
                fontFamily: fontFam,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: BLUE[700],
                mb: 0.75,
              }}
            >
              Already placed in {placements.length}
            </Typography>
            {placements.slice(0, 4).map((placement) => (
              <Typography
                key={placement.id}
                sx={{ fontFamily: fontFam, fontSize: 12.5, color: "#475569" }}
              >
                {placement.department_name}
                {placement.node_name ? ` › ${placement.node_name}` : ""}
                {placement.membership_role
                  ? ` · ${placement.membership_role}`
                  : ""}
              </Typography>
            ))}
            {placements.length > 4 && (
              <Typography
                sx={{ fontFamily: fontFam, fontSize: 12, color: "#94a3b8" }}
              >
                +{placements.length - 4} more
              </Typography>
            )}
          </Box>
        )}

        <TextField
          label="Role here"
          value={role}
          onChange={(event) => setRole(event.target.value)}
          select
          fullWidth
          helperText="Hiring managers, recruiters and interviewers get prefilled onto jobs created from this level."
          InputLabelProps={{ sx: { fontFamily: fontFam } }}
          FormHelperTextProps={{ sx: { fontFamily: fontFam } }}
        >
          {MEMBERSHIP_ROLES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Checkbox
              checked={isPrimary}
              onChange={(event) => setIsPrimary(event.target.checked)}
              size="small"
            />
          }
          label={
            <Box>
              <Typography sx={{ fontFamily: fontFam, fontSize: 14 }}>
                This is their home level
              </Typography>
              <Typography
                sx={{ fontFamily: fontFam, fontSize: 12, color: "#94a3b8" }}
              >
                One per person. Setting it here clears it wherever it was before.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", ml: 0, gap: 0.5 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ fontFamily: fontFam, color: "#64748b", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{
            fontFamily: fontFam,
            textTransform: "none",
            fontWeight: 600,
            bgcolor: BLUE[600],
            "&:hover": { bgcolor: BLUE[700] },
          }}
        >
          Add to level
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberAssignDialog;
